/**
 * AdjudicationForm — the human dictamen (APPROVED | REJECTED, justification,
 * flags_resolved checkboxes built from the automated flags).
 *
 * Write-path rule: the reviewer identity is injected SERVER-SIDE — it is NEVER
 * put in the request body. On 201 the parent reloads the detail (the effective
 * disposition flips to ADJUDICATED) and this form is replaced by the read-only
 * resolved-verdict card.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";

import { api, isApiError, useApiMutation } from "../../api/useApi";
import { FLAG_CODE_LABEL, labelOrCode } from "../../api/labels";
import type {
  AdjudicationDecision,
  AdjudicationRequest,
  ExpedienteDetail,
} from "../../api/client";
import { DECISION_LABEL, PRIORITY_LABEL } from "../../components/decisionChips";
import { KIND_LABEL, KIND_ORDER } from "./docLabels";
import { ProblemAlert } from "../../components/ProblemAlert";

export function AdjudicationForm({
  detail,
  roundId,
  expedienteId,
  onAdjudicated,
}: {
  detail: ExpedienteDetail;
  roundId: string;
  expedienteId: string;
  onAdjudicated: () => void;
}) {
  const flags = detail.automated.flags;
  const [decision, setDecision] = useState<AdjudicationDecision | "">("");
  const [justification, setJustification] = useState("");
  const [resolved, setResolved] = useState<Record<string, boolean>>({});
  // documentos_observados: the tipos the client must replace before a reenvío
  // (decision 12). Only meaningful for AWAITING_RESUBMISSION. The tipo set mirrors
  // the EXACER profile (docLabels); the server re-validates against the real profile.
  const [docsObserved, setDocsObserved] = useState<Record<string, boolean>>({});

  const { run, loading, error } = useApiMutation(
    async (body: AdjudicationRequest) => {
      const res = await api.POST(
        "/rounds/{round_id}/expedientes/{expediente_id}/adjudication",
        {
          params: { path: { round_id: roundId, expediente_id: expedienteId } },
          body,
        },
      );
      return res.data!; // AdjudicationResponse (201)
    },
  );

  // Already adjudicated → no form, just the resolved verdict.
  if (detail.effective_disposition.source === "ADJUDICATED") {
    const latest = detail.effective_disposition.latest_adjudication;
    const awaiting =
      detail.effective_disposition.decision === "AWAITING_RESUBMISSION";
    const observed = latest?.documentos_observados ?? [];
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Dictamen humano
          </Typography>
          <Alert severity="info" icon={<GppGoodOutlinedIcon />}>
            Este expediente ya fue dictaminado
            {latest ? ` por ${latest.reviewer_email}` : ""}. Verdicto:{" "}
            <strong>
              {latest ? DECISION_LABEL[latest.decision] : DECISION_LABEL[detail.effective_disposition.decision]}
            </strong>
            .{" "}
            {awaiting
              ? "Salió de la cola de revisión y está en espera de reenvío del cliente."
              : "Ya no aparece en la cola de revisión."}
          </Alert>
          {awaiting && observed.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Documentos por reemplazar:
              </Typography>
              <Typography variant="body2">
                {observed.map((tipo) => KIND_LABEL[tipo] ?? tipo).join(" · ")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  const justificationTrimmed = justification.trim();
  const canSubmit = decision !== "" && justificationTrimmed.length > 0 && !loading;

  const handleSubmit = async () => {
    if (decision === "") return;
    const flags_resolved = flags
      .map((f) => f.code)
      .filter((code) => resolved[code]);
    // Only send documentos_observados for AWAITING_RESUBMISSION (which docs the
    // client must replace); it is meaningless for an APPROVE/REJECT verdict.
    const documentos_observados =
      decision === "AWAITING_RESUBMISSION"
        ? KIND_ORDER.filter((tipo) => docsObserved[tipo])
        : [];
    const body: AdjudicationRequest = {
      decision, // APPROVED | REJECTED | AWAITING_RESUBMISSION — never reviewer identity
      justification: justificationTrimmed,
      flags_resolved,
      documentos_observados,
    };
    const result = await run(body);
    if (result) {
      // Reload detail → effective disposition becomes ADJUDICATED and this form
      // is replaced by the resolved-verdict card (the success indicator).
      onAdjudicated();
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h3" gutterBottom>
          Dictamen humano
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tu identidad de revisor se registra automáticamente; no se solicita aquí.
        </Typography>

        {/* 401/403 are handled by route guards; surface everything else. */}
        {error && (!isApiError(error) || (error.status !== 401 && error.status !== 403)) && (
          <ProblemAlert error={error} />
        )}

        <Stack spacing={2} component="form" onSubmit={(e) => e.preventDefault()}>
          <FormControl>
            <FormLabel id="adj-decision-label">Verdicto</FormLabel>
            <RadioGroup
              row
              aria-labelledby="adj-decision-label"
              value={decision}
              onChange={(e) => setDecision(e.target.value as AdjudicationDecision)}
            >
              <FormControlLabel value="APPROVED" control={<Radio />} label="Aprobar" />
              <FormControlLabel value="REJECTED" control={<Radio />} label="Rechazar" />
              <FormControlLabel
                value="AWAITING_RESUBMISSION"
                control={<Radio />}
                label="Solicitar reenvío"
              />
            </RadioGroup>
          </FormControl>

          {/* documentos_observados — which documents the client must replace. Only
              for the reenvío outcome; the server validates the tipos vs the profile. */}
          {decision === "AWAITING_RESUBMISSION" && (
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Documentos por reemplazar</FormLabel>
              <FormGroup>
                {KIND_ORDER.map((tipo) => (
                  <FormControlLabel
                    key={tipo}
                    control={
                      <Checkbox
                        checked={!!docsObserved[tipo]}
                        onChange={(e) =>
                          setDocsObserved((prev) => ({ ...prev, [tipo]: e.target.checked }))
                        }
                      />
                    }
                    label={KIND_LABEL[tipo] ?? tipo}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )}

          {flags.length > 0 && (
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Indicadores resueltos</FormLabel>
              <FormGroup>
                {flags.map((flag, i) => (
                  <FormControlLabel
                    key={`${flag.code}-${i}`}
                    control={
                      <Checkbox
                        checked={!!resolved[flag.code]}
                        onChange={(e) =>
                          setResolved((prev) => ({ ...prev, [flag.code]: e.target.checked }))
                        }
                      />
                    }
                    label={`${labelOrCode(FLAG_CODE_LABEL, flag.code)} (${PRIORITY_LABEL[flag.priority]})`}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )}

          <TextField
            label="Justificación"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            required
            multiline
            minRows={3}
            placeholder="Explica brevemente la razón del dictamen."
            helperText="Obligatoria. Queda registrada de forma inmutable junto con el dictamen."
          />

          <Box>
            <Button
              variant="contained"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
            >
              {loading ? "Registrando…" : "Registrar dictamen"}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
